import { useRef } from "react";
import { motion, useTransform, useScroll } from "framer-motion";
import { ArrowRight, PhoneCall } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const lineAnim = (i) => ({
    initial: { y: "110%" },
    animate: { y: "0%" },
    transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
});

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative overflow-hidden bg-[#112338] noise-overlay"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <img
                    src="/assets/hero-key-white.png"
                    alt=""
                    aria-hidden="true"
                    className="w-full h-full object-cover blur-[3px] scale-105"
                />
                <div className="absolute inset-0 bg-[#1F3B5B]/20 mix-blend-multiply" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#112338]/80 via-[#112338]/55 to-[#112338]/90" />
            </motion.div>
            <div className="absolute inset-0 blueprint-grid" />
            <div
                className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle at center, rgba(16,185,129,0.14) 0%, transparent 70%)",
                }}
            />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-32 lg:pt-40 lg:pb-40 min-h-[100svh] flex flex-col justify-center">
                <motion.div style={{ opacity: fade }} className="max-w-3xl">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-2"
                    >
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.2em] text-white/80">
                            Seguridad y confianza, en línea
                        </span>
                    </motion.div>

                    <h1 className="mt-7 font-display font-extrabold tracking-tight leading-[1.04] text-white text-4xl sm:text-5xl lg:text-[4.4rem]">
                        <span className="block overflow-hidden pb-1">
                            <motion.span className="block" {...lineAnim(0)}>
                                Accede a la hipoteca
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span className="block" {...lineAnim(1)}>
                                <span className="text-emerald-400">
                                    que otros no consiguen.
                                </span>
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.65 }}
                        className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
                        data-testid="hero-subtitle"
                    >
                        Estudio gratuito, asesoramiento personalizado y gestión
                        integral hasta la entrega de llaves.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.8 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-primary-cta"
                            onClick={() => scrollToId("calculadora")}
                            className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B1A2B] font-bold px-7 py-4 text-base transition-[background-color,transform] duration-200 hover:-translate-y-0.5 shadow-[0_10px_40px_rgba(16,185,129,0.35)]"
                        >
                            Simula tu hipoteca
                            <ArrowRight
                                size={18}
                                className="transition-transform duration-200 group-hover:translate-x-1"
                            />
                        </button>
                        <button
                            data-testid="hero-secondary-cta"
                            onClick={() => scrollToId("contacto")}
                            className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/60 text-white font-semibold px-7 py-4 text-base transition-[border-color,background-color] duration-200 hover:bg-white/5"
                        >
                            <PhoneCall size={17} />
                            Habla con un asesor
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.05 }}
                        className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
                    >
                        {[
                            ["10", "tipos de hipoteca"],
                            ["100%", "gestión digital"],
                            ["6", "pasos hasta las llaves"],
                        ].map(([n, label]) => (
                            <div key={label} className="flex items-baseline gap-2">
                                <span className="font-display text-2xl font-extrabold text-white">
                                    {n}
                                </span>
                                <span className="text-sm text-slate-400">
                                    {label}
                                </span>
                            </div>
                        ))}
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
